import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useUISkin } from "@/data/UISkinContext";
import CommandDashboardLens from "@/components/dashboard/CommandDashboardLens";
import type { AppointmentResult } from "@/data/appointmentResultTypes";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DollarSign, TrendingUp, Target, Users, AlertTriangle, Clock,
  ArrowRight, CheckCircle2, BarChart3, Zap, MapPin, Lightbulb,
  Shield, Megaphone, Activity,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { calculateCloseRatePercent } from "@/lib/metricsUtils";
import {
  getResultMetrics, getRevenueLeaks, getRepPerformanceSummary,
  getSourcePerformanceSummary,
} from "@/data/appointmentResultSelectors";
import { getOpportunityDisplayValue } from "@/data/opportunityRevenueSelectors";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { RevenueLeakPanel } from "@/components/RevenueLeakPanel";
import { EmptyState } from "@/components/EmptyState";
import { WhatNeedsAttentionStrip } from "@/components/WhatNeedsAttentionStrip";
import { PushModeTestBench } from "@/components/PushModeTestBench";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { useCurrentRep } from "@/hooks/useCurrentRep";
import { SalesFunnelBreakdown } from "@/components/dashboard/SalesFunnelBreakdown";
import { LogInteractionModal } from "@/components/quick-actions/LogInteractionModal";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import {
  RecommendationActionModal,
  type RecActionMode,
  type RecActionContext,
} from "@/components/dashboard/RecommendationActionModal";
import { useChannelEvents, isRecommendationDismissed } from "@/data/channelRelationship";
import { toast } from "sonner";
import type { Job } from "@/data/types";
import type { FollowUpTask } from "@/data/opportunityTypes";
import { supabase } from "@/lib/supabase";
import { isUuidLike, resolveProfileDisplayName } from "@/lib/profileDisplayName";
import { FollowUpTaskService } from "@/services/follow-up-task.service";

/* ── helpers ── */
const fmt = (n: number) =>
  n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${(n / 1_000).toFixed(0)}K` : `$${n.toFixed(0)}`;
const pct = (n: number, d: number) => (d === 0 ? 0 : Math.round((n / d) * 100));

function resultRevenue(r: AppointmentResult): number {
  if (r.pricingSnapshot) {
    return r.pricingSnapshot.bestValuePrice || r.pricingSnapshot.promoPrice || r.pricingSnapshot.standardPrice || 0;
  }
  return 0;
}

/* ── Growth Channel performance (DB-derived) ── */
interface ChannelMetrics {
  id: string;
  name: string;
  netConversion: number;
  leads: number;
  netJobs: number;
  revenue: number;
  status: "Healthy" | "Needs Attention" | "Underperforming";
}

const STATUS_STYLE: Record<string, { badge: string; text: string }> = {
  "Healthy": { badge: "bg-success/10 text-success border-success/20", text: "text-success" },
  "Needs Attention": { badge: "bg-warning/10 text-warning border-warning/20", text: "text-warning" },
  "Underperforming": { badge: "bg-destructive/10 text-destructive border-destructive/20", text: "text-destructive" },
};

/* ── Booking Efficiency (DB-derived) ── */
interface BookingEfficiency {
  assignedPct: number;
  unassignedPct: number;
  clusteringEfficiency: "High" | "Medium" | "Low";
  totalBookings: number;
  unassignedCount: number;
}

export default function CommandDashboard() {
  const { appointmentResults, salesOpportunities, followUpTasks, jobs, setFollowUpTasks } = useAppData();
  const fin$ = useFinancialPermissions();
  const { repName: CURRENT_REP } = useCurrentRep();
  const navigate = useNavigate();
  const { skin } = useUISkin();
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const [period, setPeriod] = useState<"today" | "week" | "month">("month");

  // ── Push Mode dispatcher state — keeps Live Actions in-context ──
  const [logCustomerName, setLogCustomerName] = useState<string | null>(null);
  const [logRelatedJobId, setLogRelatedJobId] = useState<string | undefined>(undefined);
  const [logFollowUpId, setLogFollowUpId] = useState<string | null>(null);
  const [rescheduleJob, setRescheduleJob] = useState<Job | null>(null);

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          [
            ...salesOpportunities.map((o) => String(o.assignedRep ?? "").trim()),
            ...appointmentResults.map((r) => String(r.assignedRep ?? "").trim()),
            ...followUpTasks.map((t) => String(t.assignedTo ?? "").trim()),
          ].filter((v) => isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }

      const [byIdRes, byAuthIdRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("id", repIds),
        supabase
          .from("profiles")
          .select("id, auth_user_id, full_name, first_name, last_name")
          .in("auth_user_id", repIds),
      ]);

      if (cancelled) return;
      const map = new Map<string, string>();
      const rows = [...(byIdRes.data ?? []), ...(byAuthIdRes.data ?? [])];
      for (const row of rows) {
        const first = String(row.first_name ?? "").trim();
        const last = String(row.last_name ?? "").trim();
        const fullName = String(row.full_name ?? "").trim() || [first, last].filter(Boolean).join(" ").trim();
        const id = String(row.id ?? "").trim();
        const authUserId = String(row.auth_user_id ?? "").trim();
        if (!fullName) continue;
        if (id) map.set(id, fullName);
        if (authUserId) map.set(authUserId, fullName);
      }
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [appointmentResults, followUpTasks, salesOpportunities]);

  const resolveRepName = (rep: string | null | undefined) =>
    resolveProfileDisplayName(rep, repNameById, "Unassigned");

  /** Push Mode runner: open a modal for the top item; warn if no mapping. */
  const runAction = (
    actionType: "missing-result" | "overdue-followup" | "reschedule",
    payload: { customerName?: string; jobId?: string; followUpId?: string; job?: Job | null },
  ) => {
    if (actionType === "missing-result") {
      if (!payload.customerName) {
        console.warn("[Push Mode] Missing modal mapping for action: missing-result (no customerName in payload)");
        toast.error("No record available to log");
        return;
      }
      setLogFollowUpId(null);
      setLogRelatedJobId(payload.jobId);
      setLogCustomerName(payload.customerName);
      return;
    }
    if (actionType === "overdue-followup") {
      if (!payload.customerName || !payload.followUpId) {
        console.warn("[Push Mode] Missing modal mapping for action: overdue-followup");
        toast.error("No follow-up available");
        return;
      }
      setLogRelatedJobId(payload.jobId);
      setLogFollowUpId(payload.followUpId);
      setLogCustomerName(payload.customerName);
      return;
    }
    if (actionType === "reschedule") {
      if (!payload.job) {
        console.warn("[Push Mode] Missing modal mapping for action: reschedule (linked Job not found)");
        toast.error("Linked job not found — open Appointment Results");
        return;
      }
      setRescheduleJob(payload.job);
      return;
    }
    console.warn(`[Push Mode] Unknown action: ${actionType}`);
  };

  /** Mark a follow-up task complete after the user logs the touch (DB first). */
  const handleLogClose = (open: boolean) => {
    if (open) return;

    const clearModalState = () => {
      setLogFollowUpId(null);
      setLogCustomerName(null);
      setLogRelatedJobId(undefined);
    };

    if (!logFollowUpId) {
      clearModalState();
      return;
    }

    const completeFollowUp = async () => {
      const completedAt = new Date().toISOString();
      const task = followUpTasks.find((t) => t.id === logFollowUpId);
      const completedBy = String(task?.assignedTo ?? "").trim() || "system";
      const { error } = await FollowUpTaskService.updateStatus(
        logFollowUpId,
        "completed",
        completedAt,
        completedBy,
      );
      if (error) {
        toast.error("Failed to save follow-up completion");
        return;
      }
      setFollowUpTasks((prev) =>
        prev.map((t) =>
          t.id === logFollowUpId
            ? { ...t, status: "completed" as const, completedAt }
            : t,
        ),
      );
      clearModalState();
    };

    void completeFollowUp();
  };

  // Sales reps see only their own pipeline data
  const scopedOpps = fin$.scopedToOwn
    ? salesOpportunities.filter(
        (o) => resolveRepName(o.assignedRep) === CURRENT_REP
      )
    : salesOpportunities;
  const scopedResults = fin$.scopedToOwn
    ? appointmentResults.filter(
        (r) => resolveRepName(r.assignedRep) === CURRENT_REP
      )
    : appointmentResults;
  const scopedFollowUps = fin$.scopedToOwn
    ? followUpTasks.filter(
        (t) => resolveRepName(t.assignedTo) === CURRENT_REP
      )
    : followUpTasks;

  const periodLabel = period === "today" ? "Today" : period === "week" ? "Last 7 days" : "Last 30 days";

  const bounds = useMemo(() => {
    const d = new Date();
    if (period === "today") {
      d.setHours(0, 0, 0, 0);
    } else if (period === "week") {
      d.setDate(d.getDate() - 7);
      d.setHours(0, 0, 0, 0);
    } else {
      d.setDate(d.getDate() - 30);
      d.setHours(0, 0, 0, 0);
    }
    return d.toISOString();
  }, [period]);

  // Helper: get opportunity creation/last-update date — use stageHistory for accuracy
  const getOppDate = (o: { stageHistory?: { changedAt: string }[] }) => {
    const hist = o.stageHistory ?? [];
    // Use most recent stage change so a current-week deal counts in "Today" if it moved today
    return hist.length > 0 ? hist[hist.length - 1].changedAt : "";
  };

  // Filter all data by selected period
  const filteredOpps = useMemo(
    () => scopedOpps.filter((o) => getOppDate(o) >= bounds),
    [scopedOpps, bounds],
  );

  const filteredResults = useMemo(
    () => scopedResults.filter((r) => (r.submittedAt || "") >= bounds),
    [scopedResults, bounds],
  );

  const filteredFollowUps = useMemo(
    () => scopedFollowUps.filter((t) => (t.createdAt || "") >= bounds),
    [scopedFollowUps, bounds],
  );

  const channelMetrics = useMemo<ChannelMetrics[]>(() => {
    const sourceSummary = getSourcePerformanceSummary(filteredResults, jobs);
    if (sourceSummary.length === 0) return [];
    const getJobDate = (j: Job): string =>
      String(j.updatedAt ?? j.createdAt ?? j.scheduledInstallDate ?? "");
    const netJobsInPeriod = jobs.filter(
      (j) =>
        getJobDate(j) >= bounds &&
        (j.currentStage === "Completed" ||
          j.rescissionStatus === "Cleared for Production"),
    );

    const sourceByOppId = new Map<string, string>();
    const sourceByJobId = new Map<string, string>();
    for (const o of filteredOpps) {
      const source = String((o as { leadSource?: string }).leadSource ?? "").trim();
      sourceByOppId.set(o.id, source);
      if (o.linkedJobId) sourceByJobId.set(o.linkedJobId, source);
    }

    const netJobsBySource = new Map<string, number>();
    for (const job of netJobsInPeriod) {
      const source =
        sourceByOppId.get(String(job.opportunityId ?? "")) ??
        sourceByJobId.get(job.id) ??
        "";
      if (!source) continue;
      netJobsBySource.set(source, (netJobsBySource.get(source) ?? 0) + 1);
    }

    return sourceSummary.map((s) => {
      const netJobs = netJobsBySource.get(s.source) ?? 0;
      const leads = Math.max(0, s.leads);
      const netConversion = leads > 0 ? Math.round((netJobs / leads) * 100) : 0;
      const status: ChannelMetrics["status"] =
        netConversion >= 40 ? "Healthy" : netConversion >= 20 ? "Needs Attention" : "Underperforming";
      return {
        id: s.source.toLowerCase().replace(/\s+/g, "-"),
        name: s.source,
        netConversion,
        leads,
        netJobs,
        revenue: s.revenue,
        status,
      };
    }).sort((a, b) => b.revenue - a.revenue).slice(0, 3);
  }, [filteredResults, filteredOpps, jobs, bounds]);

  const bookingEfficiency = useMemo<BookingEfficiency>(() => {
    const scheduledJobs = jobs.filter((j) => Boolean(j.scheduledInstallDate));
    const totalBookings = scheduledJobs.length;
    if (totalBookings === 0) {
      return {
        assignedPct: 0,
        unassignedPct: 0,
        clusteringEfficiency: "Low",
        totalBookings: 0,
        unassignedCount: 0,
      };
    }
    const assignedCount = scheduledJobs.filter((j) => Boolean(j.assignedCrew || j.assignedSubcontractor)).length;
    const unassignedCount = totalBookings - assignedCount;
    const unassignedPct = Math.round((unassignedCount / totalBookings) * 100);

    const byDate = new Map<string, number>();
    scheduledJobs.forEach((j) => {
      const key = String(j.scheduledInstallDate ?? "");
      byDate.set(key, (byDate.get(key) ?? 0) + 1);
    });
    const clusteredDays = [...byDate.values()].filter((n) => n > 1).length;
    const clusterRatio = byDate.size > 0 ? clusteredDays / byDate.size : 0;
    const clusteringEfficiency: BookingEfficiency["clusteringEfficiency"] =
      clusterRatio >= 0.5 ? "High" : clusterRatio >= 0.25 ? "Medium" : "Low";

    return {
      assignedPct: Math.round((assignedCount / totalBookings) * 100),
      unassignedPct,
      clusteringEfficiency,
      totalBookings,
      unassignedCount,
    };
  }, [jobs]);

  const hasFilteredData =
    filteredOpps.length > 0 || filteredResults.length > 0 || filteredFollowUps.length > 0;

  /* ── Revenue Quality (period-filtered) ── */
  const revenueQuality = useMemo(() => {
    const getJobDate = (j: Job): string =>
      String(j.updatedAt ?? j.createdAt ?? j.scheduledInstallDate ?? "");
    const jobsInPeriod = jobs.filter((j) => getJobDate(j) >= bounds);
    const netJobsInPeriod = jobsInPeriod.filter(
      (j) =>
        j.currentStage === "Completed" ||
        j.rescissionStatus === "Cleared for Production",
    );

    const won = filteredOpps.filter(o => o.stage === "Closed Won");
    const totalWon = won.length;
    const pending = won.filter(o => !o.wonOutcome || o.wonOutcome === "pending");
    const totalLeads = filteredOpps.length;
    // Won and pending remain opportunity-scoped; net is job-scoped because
    // a job only exists after a won quote/opportunity.
    const wonRevenue = won.reduce((s, o) => s + getOpportunityDisplayValue(o, jobs), 0);
    const netRevenue = netJobsInPeriod.reduce((s, j) => s + getJobDisplayValue(j), 0);
    const pendingRevenue = pending.reduce((s, o) => s + getOpportunityDisplayValue(o, jobs), 0);
    const netCount = netJobsInPeriod.length;
    const netConversion = totalLeads > 0 ? Math.round((netCount / totalLeads) * 100) : 0;
    return { totalWon, netCount, pendingCount: pending.length, wonRevenue, netRevenue, pendingRevenue, netConversion, totalLeads };
  }, [filteredOpps, jobs, bounds]);

  /* ── Rep Performance Intelligence ── */
  const repInsights = useMemo(() => {
    return getRepPerformanceSummary(filteredResults, jobs).map((rep) => {
      const signal: "strong_on_quality" | "underperforming_on_quality" | "consistent" =
        rep.closeRate >= 50 ? "strong_on_quality" : rep.closeRate <= 20 ? "underperforming_on_quality" : "consistent";
      const insightSeverity: "positive" | "neutral" | "caution" =
        signal === "strong_on_quality" ? "positive" : signal === "underperforming_on_quality" ? "caution" : "neutral";
      const insight =
        signal === "strong_on_quality"
          ? "Strong close rate in current period"
          : signal === "underperforming_on_quality"
          ? "Lower close rate — review pitch quality and follow-up timing"
          : "Performance within expected range";
      return {
        repName: rep.rep,
        netConversion: rep.closeRate,
        highFitConversion: rep.closeRate,
        signal,
        insightSeverity,
        insight,
      };
    }).slice(0, 4);
  }, [filteredResults, jobs]);

  /* ── Live Actions (period-filtered) ── */
  const now = new Date();
  const actions = useMemo(() => {
    const missingResults = filteredOpps.filter(
      (o) => o.stage === "Appointment Completed" && !filteredResults.some((r) => r.opportunityId === o.id),
    );
    const overdueFollowUps = filteredFollowUps.filter(
      (t) => t.status !== "completed" && new Date(t.dueDate) < now,
    );
    const reschedulesNeeded = filteredResults.filter(
      (r) => r.resultType === "rescheduled" && !r.rescheduleDatetime,
    );
    return { missingResults, overdueFollowUps, reschedulesNeeded };
  }, [filteredOpps, filteredResults, filteredFollowUps]);

  const actionCount = actions.missingResults.length + actions.overdueFollowUps.length + actions.reschedulesNeeded.length;

  /* ── System Recommendations — actionable via Apply / Dismiss / Log touch.
     Each rec carries a stable key + entity (channel/rep/lever) so the
     operator can capture decisions, dismiss with reason+snooze, or log a
     touch — all persisted to the channel relationship history. ── */
  type Rec = {
    key: string;
    channelId: string;
    channelName: string;
    text: string;
    decisionLabel: string;
    severity: "positive" | "neutral" | "caution";
  };
  const channelEvents = useChannelEvents();
  const recommendations = useMemo<Rec[]>(() => {
    void channelEvents;
    const recs: Rec[] = [];

    const topChannel = channelMetrics[0];
    if (topChannel && topChannel.status === "Healthy") {
      recs.push({
        key: `channel:${topChannel.id}:increase-budget`,
        channelId: topChannel.id,
        channelName: topChannel.name,
        text: `Increase ${topChannel.name} focus — strong net conversion (${topChannel.netConversion}%) and ${topChannel.netJobs} net jobs`,
        decisionLabel: "Increase budget",
        severity: "positive",
      });
    }
    const worstChannel = [...channelMetrics].sort((a, b) => a.netConversion - b.netConversion)[0];
    if (worstChannel && worstChannel.status === "Underperforming") {
      recs.push({
        key: `channel:${worstChannel.id}:review-spend`,
        channelId: worstChannel.id,
        channelName: worstChannel.name,
        text: `Review ${worstChannel.name} lead quality — net conversion is only ${worstChannel.netConversion}%`,
        decisionLabel: "Review spend",
        severity: "caution",
      });
    }

    const topRep = repInsights.find(r => r.signal === "strong_on_quality");
    if (topRep) {
      recs.push({
        key: `rep:${topRep.repName}:route-high-fit`,
        channelId: `rep:${topRep.repName}`,
        channelName: topRep.repName,
        text: `Route more high-quality leads to ${topRep.repName} — ${topRep.highFitConversion}% high-fit conversion`,
        decisionLabel: "Route high-fit leads",
        severity: "positive",
      });
    }
    const underRep = repInsights.find(r => r.signal === "underperforming_on_quality");
    if (underRep) {
      recs.push({
        key: `rep:${underRep.repName}:coach`,
        channelId: `rep:${underRep.repName}`,
        channelName: underRep.repName,
        text: `Review ${underRep.repName}'s approach on qualified leads — ${underRep.highFitConversion}% high-fit conversion`,
        decisionLabel: "Coach rep",
        severity: "caution",
      });
    }

    if (bookingEfficiency.unassignedPct > 15) {
      recs.push({
        key: `ops:booking-radius:tighten`,
        channelId: "ops:booking-radius",
        channelName: "Scheduling coverage",
        text: `Reduce unassigned scheduled jobs — ${bookingEfficiency.unassignedPct}% of booked installs have no installer assigned`,
        decisionLabel: "Improve assignment",
        severity: "caution",
      });
    }

    return recs.filter((r) => !isRecommendationDismissed(r.key)).slice(0, 4);
  }, [repInsights, channelEvents, channelMetrics, bookingEfficiency.unassignedPct]);

  /* ── Recommendation action modal state ── */
  const [recModalMode, setRecModalMode] = useState<RecActionMode>("apply");
  const [recModalContext, setRecModalContext] = useState<RecActionContext | null>(null);
  const openRecModal = (mode: RecActionMode, rec: Rec) => {
    setRecModalMode(mode);
    setRecModalContext({
      channelId: rec.channelId,
      channelName: rec.channelName,
      recommendationKey: rec.key,
      recommendationText: rec.text,
      decisionLabel: rec.decisionLabel,
      severity: rec.severity,
    });
  };

  // Cockpit skin — render the lens-switcher wrapper. For super_admin/owner
  // it adds a Strategic | Operations toggle; for everyone else it renders
  // KoredorCommandDashboard exactly as before. Classic skin falls through
  // to the original layout below — DO NOT TOUCH.
  if (skin === "cockpit") {
    return <CommandDashboardLens />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Command Dashboard</h1>
          <p className="text-sm text-muted-foreground">Operator performance, revenue truth, and next actions</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1">
          {(["today", "week", "month"] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={cn(
                "px-3 py-1 text-xs font-medium rounded-md transition-colors",
                period === p ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
              )}
            >
              {p === "today" ? "Today" : p === "week" ? "7 Days" : "30 Days"}
            </button>
          ))}
        </div>
      </div>

      {/* Active filter banner — confirms the period is wired and explains empty states */}
      <div className="flex items-center justify-between text-[11px] text-muted-foreground bg-muted/30 border rounded-md px-3 py-1.5">
        <span>
          Showing data for <span className="font-semibold text-foreground">{periodLabel}</span>
          {!hasFilteredData && (
            <span className="ml-2 text-warning">
              — no opportunities, results, or follow-ups in this range
            </span>
          )}
        </span>
        <span className="text-muted-foreground/60">
          {filteredOpps.length} opp · {filteredResults.length} results · {filteredFollowUps.length} follow-ups
        </span>
      </div>

      {/* Standardized empty state when period yields zero data */}
      {!hasFilteredData && (
        <Card>
          <CardContent className="p-0">
            <EmptyState
              icon={Activity}
              title="Nothing recorded yet for this period"
              description="Switch to a wider range, or take an action to start populating the command dashboard."
              actionLabel="View Opportunities"
              onAction={() => navigate("/opportunities")}
            />
          </CardContent>
        </Card>
      )}

      {/* ═══ PUSH MODE TEST BENCH (QA) ═══ */}
      {import.meta.env.DEV && <PushModeTestBench />}

      {/* ═══ WHAT NEEDS ATTENTION ═══ */}
      <WhatNeedsAttentionStrip />

      {/* ═══ SECTION 1: GROWTH PERFORMANCE (Top Row — trailing 30d benchmark, not period-filtered) ═══ */}
      <div>
        <SectionLabel icon={Megaphone} label="Growth Performance · trailing 30 days" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          {channelMetrics.map((ch, i) => {
            const style = STATUS_STYLE[ch.status];
            const isTop = i === 0;
            return (
              <Card key={ch.id} className={cn(isTop && "ring-1 ring-primary/15")}>
                <CardContent className="pt-4 pb-3 px-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{ch.name}</span>
                      {isTop && (
                        <Badge variant="outline" className="text-[8px] px-1.5 py-0 bg-primary/5 text-primary border-primary/20">
                          Top
                        </Badge>
                      )}
                    </div>
                    <Badge variant="outline" className={cn("text-[9px]", style.badge)}>{ch.status}</Badge>
                  </div>
                  {/* Net conversion — Hero Metric */}
                  <div className="text-center py-1">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">Net Conversion</p>
                    <p className={cn("text-2xl font-bold", style.text)}>{ch.netConversion}%</p>
                  </div>
                  {/* Supporting metrics */}
                  <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                    <span>Revenue: <span className="font-semibold text-foreground">{fmt(ch.revenue)}</span></span>
                    <span>{ch.leads} leads · {ch.netJobs} net</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {channelMetrics.length === 0 && (
            <Card>
              <CardContent className="pt-4 pb-3 px-4">
                <p className="text-xs text-muted-foreground">No source performance data in the selected period.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ═══ SECTION 2 + 3: REVENUE QUALITY + REP PERFORMANCE (Middle Row) ═══ */}
      <div className={cn("grid gap-4", fin$.viewRepLeaderboard ? "md:grid-cols-2" : "md:grid-cols-1")}>
        {/* Revenue Quality */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center justify-between gap-2">
              <span className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-chart-2" /> Revenue Quality
              </span>
              <Badge variant="outline" className="text-[9px] font-normal">{periodLabel}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => navigate("/opportunities?stage=Closed%20Won")}
                className="rounded-lg border p-3 text-center hover:bg-muted/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Open all Closed Won opportunities"
              >
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Won</p>
                <p className="text-lg font-bold text-foreground">{revenueQuality.totalWon}</p>
                <p className="text-[10px] text-muted-foreground">{fmt(revenueQuality.wonRevenue)}</p>
              </button>
              <button
                type="button"
                onClick={() => navigate("/opportunities?wonOutcome=pending")}
                className="rounded-lg border p-3 text-center border-warning/20 bg-warning/5 hover:bg-warning/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Resolve pending won outcomes (Net / Canceled / Declined)"
              >
                <p className="text-[10px] text-warning font-medium uppercase tracking-wider">Pending</p>
                <p className="text-lg font-bold text-warning">{revenueQuality.pendingCount}</p>
                <p className="text-[10px] text-muted-foreground">{fmt(revenueQuality.pendingRevenue)}</p>
              </button>
              <button
                type="button"
                onClick={() => navigate("/opportunities?wonOutcome=net")}
                className="rounded-lg border p-3 text-center border-success/20 bg-success/5 hover:bg-success/10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                title="Open finalized Net opportunities"
              >
                <p className="text-[10px] text-success font-medium uppercase tracking-wider">Net</p>
                <p className="text-lg font-bold text-success">{revenueQuality.netCount}</p>
                <p className="text-[10px] text-muted-foreground">{fmt(revenueQuality.netRevenue)}</p>
              </button>
            </div>
            {/* Net Conversion */}
            <div className="flex items-center justify-between px-1">
              <span className="text-xs text-muted-foreground">Net Conversion</span>
              <span className="text-sm font-bold text-foreground">{revenueQuality.netConversion}%
                <span className="text-[10px] text-muted-foreground ml-1 font-normal">({revenueQuality.netCount} / {revenueQuality.totalLeads} leads)</span>
              </span>
            </div>
            {/* Push CTA — only when there is meaningful Pending revenue to resolve */}
            {revenueQuality.pendingCount > 0 && (
              <div className="flex items-center justify-between rounded-md border border-warning/30 bg-warning/10 px-3 py-2">
                <div className="flex flex-col">
                  <span className="text-[11px] font-semibold text-warning">
                    {revenueQuality.pendingCount} pending {revenueQuality.pendingCount === 1 ? "deal" : "deals"} · {fmt(revenueQuality.pendingRevenue)}
                  </span>
                  <span className="text-[10px] text-muted-foreground">Resolve outcomes to confirm Net revenue</span>
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/opportunities?wonOutcome=pending")}
                  className="text-xs font-semibold text-warning hover:underline px-2 py-1 rounded-md hover:bg-warning/10"
                >
                  Resolve pending
                </button>
              </div>
            )}
            <p className="text-[10px] text-muted-foreground/70 italic px-1">
              Net represents finalized jobs after cancellations and financing outcomes.
            </p>
          </CardContent>
        </Card>

        {/* Rep Performance — cross-rep leaderboard; hidden from sales rep */}
        {fin$.viewRepLeaderboard && (
          <Card>
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-chart-1" /> Rep Performance
              </CardTitle>
              <button
                type="button"
                onClick={() => navigate("/rep-scorecards")}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                Open Rep Scorecards →
              </button>
            </CardHeader>
          <CardContent className="space-y-2">
              {repInsights.slice(0, 4).map((rep) => {
                const isUnder = rep.insightSeverity === "caution";
                return (
                  <div key={rep.repName} className="flex items-start gap-3 rounded-lg border p-2.5">
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      <span className="text-[9px] font-bold text-primary">{resolveRepName(rep.repName).split(" ").map(n => n[0]).join("")}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-foreground"> {resolveRepName(rep.repName)}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-foreground">{rep.netConversion}%</span>
                          <span className={cn(
                            "text-[10px] font-medium",
                            rep.highFitConversion >= 50 ? "text-success" : rep.highFitConversion >= 35 ? "text-warning" : "text-destructive"
                          )}>{rep.highFitConversion}% HF</span>
                        </div>
                      </div>
                      <p className={cn(
                        "text-[10px] mt-0.5 leading-snug",
                        rep.insightSeverity === "positive" ? "text-success" :
                        rep.insightSeverity === "caution" ? "text-warning" : "text-muted-foreground"
                      )}>{rep.insight}</p>
                      {/* Push CTA — coach underperformers in-context, scorecard for everyone */}
                      <div className="flex items-center gap-2 mt-1.5">
                        {isUnder && (
                          <button
                            type="button"
                            onClick={() => {
                              setLogFollowUpId(null);
                              setLogRelatedJobId(undefined);
                              setLogCustomerName(rep.repName);
                            }}
                            className="text-[10px] font-semibold text-warning hover:underline px-1.5 py-0.5 rounded hover:bg-warning/10"
                            title={`Log a coaching touch with ${rep.repName}`}
                          >
                            Coach rep
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => navigate(`/rep-scorecards?rep=${encodeURIComponent(rep.repName)}`)}
                          className="text-[10px] text-muted-foreground hover:text-foreground hover:underline"
                        >
                          View scorecard
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ═══ SECTION 4: BOOKING EFFICIENCY + SYSTEM RECOMMENDATIONS (Bottom Row) ═══ */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Booking Efficiency */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <MapPin className="h-4 w-4 text-chart-5" /> Booking Efficiency
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-lg border p-3 text-center">
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Assigned</p>
                <p className="text-lg font-bold text-foreground">{bookingEfficiency.assignedPct}%</p>
              </div>
              <div className={cn(
                "rounded-lg border p-3 text-center",
                bookingEfficiency.unassignedPct > 15 ? "border-warning/20 bg-warning/5" : ""
              )}>
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Unassigned</p>
                <p className={cn("text-lg font-bold", bookingEfficiency.unassignedPct > 15 ? "text-warning" : "text-foreground")}>
                  {bookingEfficiency.unassignedPct}%
                </p>
              </div>
              <div className="rounded-lg border p-3 text-center">
                <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Clustering</p>
                <p className={cn(
                  "text-lg font-bold",
                  bookingEfficiency.clusteringEfficiency === "High" ? "text-success" :
                  bookingEfficiency.clusteringEfficiency === "Medium" ? "text-warning" : "text-destructive"
                )}>{bookingEfficiency.clusteringEfficiency}</p>
              </div>
            </div>
            <p className="text-[10px] text-muted-foreground/70 italic px-1">
              {bookingEfficiency.unassignedCount} of {bookingEfficiency.totalBookings} scheduled jobs still need installer assignment.
            </p>
          </CardContent>
        </Card>

        {/* System Recommendations */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-chart-4" /> System Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {recommendations.map((rec, i) => {
              const toneClasses =
                rec.severity === "positive" ? "bg-success/5 border-success/15" :
                rec.severity === "caution" ? "bg-warning/5 border-warning/15" :
                "bg-muted/30 border-border";
              const ctaToneClasses =
                rec.severity === "positive" ? "text-success hover:bg-success/10" :
                rec.severity === "caution" ? "text-warning hover:bg-warning/10" :
                "text-primary hover:bg-primary/5";
              return (
                <div key={i} className={cn("rounded-lg border px-3 py-2.5 flex items-start gap-2", toneClasses)}>
                  {rec.severity === "positive" ? <CheckCircle2 className="h-3.5 w-3.5 text-success mt-0.5 shrink-0" /> :
                   rec.severity === "caution" ? <AlertTriangle className="h-3.5 w-3.5 text-warning mt-0.5 shrink-0" /> :
                   <Activity className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] leading-snug text-foreground">{rec.text}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <button
                        type="button"
                        onClick={() => openRecModal("apply", rec)}
                        className={cn("text-[11px] font-semibold px-2 py-0.5 rounded-md transition-colors", ctaToneClasses)}
                      >
                        Apply
                      </button>
                      <button
                        type="button"
                        onClick={() => openRecModal("touch", rec)}
                        className="text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 px-2 py-0.5 rounded-md transition-colors"
                      >
                        Log touch
                      </button>
                      <button
                        type="button"
                        onClick={() => openRecModal("dismiss", rec)}
                        className="text-[10px] text-muted-foreground hover:text-foreground hover:bg-muted/50 px-2 py-0.5 rounded-md transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* ═══ SECTION 5: LIVE ACTIONS + REVENUE LEAK (Action Layer) ═══ */}
      <div className="grid md:grid-cols-2 gap-4">
        <RevenueLeakPanel />

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Clock className="h-4 w-4 text-chart-5" />
              Live Actions
              <Badge variant="outline" className="text-[9px] font-normal ml-1">{periodLabel}</Badge>
              {actionCount > 0 && <Badge variant="destructive" className="ml-auto text-xs">{actionCount}</Badge>}
            </CardTitle>
            <p className="text-[10px] text-muted-foreground mt-1">
              Push Mode — primary CTA resolves the top item in place. Use the secondary link for the full queue.
            </p>
          </CardHeader>
          <CardContent className="space-y-2">
            <PushActionRow
              label="Missing Results"
              count={actions.missingResults.length}
              ctaLabel="Log outcome"
              hint="Capture appointment outcome for top opportunity"
              onPrimary={() => {
                const top = actions.missingResults[0];
                if (!top) return;
                runAction("missing-result", { customerName: top.customerName });
              }}
              onSecondary={() => navigate("/opportunities")}
            />
            <PushActionRow
              label="Overdue Follow-Ups"
              count={actions.overdueFollowUps.length}
              ctaLabel="Log touch"
              hint="Log a call/text for the oldest overdue task — marks it complete"
              onPrimary={() => {
                const top = actions.overdueFollowUps[0];
                if (!top) return;
                runAction("overdue-followup", {
                  customerName: top.customerName,
                  followUpId: top.id,
                });
              }}
              onSecondary={() => navigate("/follow-ups")}
            />
            <PushActionRow
              label="Reschedules Needed"
              count={actions.reschedulesNeeded.length}
              ctaLabel="Reschedule"
              hint="Open Push Mode reschedule for the top result"
              onPrimary={() => {
                const top = actions.reschedulesNeeded[0];
                if (!top) return;
                const linkedJob = jobs.find((j) => j.customerName === top.customerName) ?? null;
                runAction("reschedule", { job: linkedJob });
              }}
              onSecondary={() => navigate("/appointment-results")}
            />
          </CardContent>
        </Card>
      </div>

      {/* ═══ SALES FUNNEL BREAKDOWN (moved from Intelligence Dashboard) ═══ */}
      <SalesFunnelBreakdown />

      {/* ── Push Mode modals (in-context execution) ── */}
      <LogInteractionModal
        open={!!logCustomerName}
        onOpenChange={handleLogClose}
        customerName={logCustomerName ?? ""}
        relatedJobId={logRelatedJobId}
      />
      <RescheduleModal
        open={!!rescheduleJob}
        onOpenChange={(o) => !o && setRescheduleJob(null)}
        job={rescheduleJob}
      />
      <RecommendationActionModal
        open={!!recModalContext}
        onOpenChange={(o) => !o && setRecModalContext(null)}
        mode={recModalMode}
        context={recModalContext}
      />
    </div>
  );
}

/* ── Sub-components ── */

function SectionLabel({ icon: Icon, label }: { icon: React.ElementType; label: string }) {
  return (
    <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5" /> {label}
    </p>
  );
}

function ActionRow({
  label,
  count,
  onClick,
  hint,
}: {
  label: string;
  count: number;
  onClick: () => void;
  hint?: string;
}) {
  const isClear = count === 0;
  return (
    <button
      onClick={isClear ? undefined : onClick}
      disabled={isClear}
      title={hint}
      className={cn(
        "flex items-center justify-between w-full px-3 py-2 rounded-md text-sm transition-colors",
        isClear ? "cursor-default opacity-70" : "hover:bg-muted/50",
      )}
    >
      <span className="flex flex-col items-start text-left">
        <span className="text-foreground">{label}</span>
        {hint && !isClear && (
          <span className="text-[10px] text-muted-foreground">{hint}</span>
        )}
      </span>
      <div className="flex items-center gap-2">
        {count > 0 ? (
          <Badge variant="destructive" className="text-xs">{count}</Badge>
        ) : (
          <CheckCircle2 className="h-4 w-4 text-chart-2" />
        )}
        {!isClear && <ArrowRight className="h-3 w-3 text-muted-foreground" />}
      </div>
    </button>
  );
}

/**
 * PushActionRow — Push Mode CTA. Primary button executes in-context (modal/direct);
 * secondary "Open queue" link is the only navigation escape hatch.
 */
function PushActionRow({
  label,
  count,
  ctaLabel,
  hint,
  onPrimary,
  onSecondary,
}: {
  label: string;
  count: number;
  ctaLabel: string;
  hint?: string;
  onPrimary: () => void;
  onSecondary: () => void;
}) {
  const isClear = count === 0;
  return (
    <div
      className={cn(
        "flex items-center justify-between w-full px-3 py-2 rounded-md text-sm",
        isClear ? "opacity-70" : "hover:bg-muted/50 transition-colors",
      )}
    >
      <div className="flex flex-col items-start text-left min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-foreground">{label}</span>
          {count > 0 ? (
            <Badge variant="destructive" className="text-[10px] px-1.5 py-0 h-4">{count}</Badge>
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5 text-chart-2" />
          )}
        </div>
        {hint && !isClear && (
          <span className="text-[10px] text-muted-foreground">{hint}</span>
        )}
      </div>
      {!isClear && (
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onPrimary}
            className="text-xs font-semibold text-primary hover:underline px-2 py-1 rounded-md hover:bg-primary/5"
          >
            {ctaLabel}
          </button>
          <button
            onClick={onSecondary}
            className="text-[10px] text-muted-foreground hover:text-foreground hover:underline"
            title="Open the full queue (secondary)"
          >
            Open queue
          </button>
        </div>
      )}
    </div>
  );
}
